import { useState, useEffect, useMemo, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, AlertCircle } from 'lucide-react'
import { useNavigate, Link } from 'react-router-dom'
import { cn } from '@lib/utils/helpers'
import { Tooltip } from '@/components/ui/Tooltip'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  loginUser,
  selectIsAuthenticated,
  selectUser,
  clearError,
} from '@/features/auth/authSlice'
import { RoleSelectionDialog } from '@/features/auth/components/RoleSelectionDialog'
import {
  IosLoadingOverlay,
} from '@/features/auth/components/IosActivityIndicator'
import {
  type UserRole,
  type LoginResponse,
} from '@/types/auth'
import { STORAGE_KEYS } from '@/lib/utils/storage'
import { isSandboxEnvironment } from '@config/env'
import {
  clearTempUserRole,
  completeRoleChange,
  navigateAfterRoleChange,
  shouldAutoLoginWithCurrentRole,
} from '@/features/auth/utils/postLoginFlow'
import { fetchApplications, fetchApplicationById } from '@/features/applicant/applicationSlice'
import { showWarning } from '@/lib/utils/notifications'
import { AUTH_CONTENT, getAuthCopyright } from '../constants/authContent'
import { SandboxBanner } from '../components/SandboxBanner'
import { loadOrgData } from '../services/rolesConfigApi'
import { resolveLoginErrorPopup } from '../utils/loginErrors'
import { showLoginValidationMessage } from '../utils/showLoginValidationMessage'
import type { LoginFormData } from '../types'

const loginCopy = AUTH_CONTENT.login

export function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [roleError, setRoleError] = useState<string | null>(null)
  const [showRoleSelection, setShowRoleSelection] = useState(false)
  /** Blocks the "already logged in" redirect until role selection / OTP completes. */
  const [isPostLoginFlow, setIsPostLoginFlow] = useState(false)
  const [loginLogoSrc, setLoginLogoSrc] = useState<string>(loginCopy.defaultLogoSrc)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)

  const loginSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, loginCopy.validation.emailRequired)
          .email(loginCopy.validation.emailInvalid),
        password: z
          .string()
          .min(1, loginCopy.validation.passwordRequired)
          .min(6, loginCopy.validation.passwordMinLength),
      }),
    []
  )

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Credential-login only — skip when role dialog is open so it does not stack with
  // the dialog spinner (changeRole also sets global auth.isLoading).
  const showLoginSpinner = isSubmitting && !showRoleSelection

  const typedEmail = watch('email')

  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  // Angular LoadUserRoles → org_data.org_image (Login Page Logo from Organization config)
  useEffect(() => {
    let cancelled = false
    void loadOrgData().then((orgData) => {
      if (cancelled) return
      const orgImage = orgData.org_image?.trim()
      if (orgImage) setLoginLogoSrc(orgImage)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Show the "session expired" notice persisted by the 401 interceptor before its hard redirect.
  useEffect(() => {
    let notice: string | null = null
    try {
      notice = sessionStorage.getItem('session_expired_notice')
      if (notice) sessionStorage.removeItem('session_expired_notice')
    } catch {
      /* ignore */
    }
    if (notice) showWarning(notice)
  }, [])

  const finishPostLoginFlow = useCallback(() => {
    setIsPostLoginFlow(false)
  }, [])

  const handleRoleChangeSuccess = useCallback(
    async (role: UserRole) => {
      setShowRoleSelection(false)
      finishPostLoginFlow()
      await navigateAfterRoleChange(role, navigate, dispatch)
    },
    [dispatch, finishPostLoginFlow, navigate],
  )

  // Returning users already logged in with a role — skip login page
  useEffect(() => {
    if (
      !isAuthenticated ||
      showRoleSelection ||
      isPostLoginFlow ||
      !user?.current_role?.role_id
    ) {
      return
    }

    const storedRole = localStorage.getItem(STORAGE_KEYS.userRole)
    if (storedRole === 'admin') {
      navigate('/dashboard', { replace: true })
      return
    }

    const checkInvitesAndRedirect = async () => {
      try {
        const applications = await dispatch(fetchApplications()).unwrap()
        if (applications.length === 1) {
          const app = applications[0]
          if (app.applicant_id) {
            await dispatch(fetchApplicationById(app.applicant_id)).unwrap()
          }
          navigate('/applicant/application/about', { replace: true })
        } else {
          navigate('/applicant/dashboard', { replace: true })
        }
      } catch (error) {
        console.error('Failed to fetch applications:', error)
        navigate('/applicant/dashboard', { replace: true })
      }
    }

    void checkInvitesAndRedirect()
  }, [isAuthenticated, showRoleSelection, isPostLoginFlow, user?.current_role?.role_id, navigate, dispatch])

  /**
   * Angular `login.component.ts` roleSelection — auto-login when current_role is a
   * non-Liberty-staff role; otherwise show UserRoleSelectionComponent dialog.
   */
  const handleLoginSuccess = async (result: LoginResponse) => {
    if (!result.token || !result.user_data) return

    const userData = result.user_data
    // Fresh login always starts at the role picker — never resume OTP from a prior attempt.
    clearTempUserRole()

    if (shouldAutoLoginWithCurrentRole(userData)) {
      const role = userData.current_role!

      if (role.role_name === 'ROLE_RENTER') {
        finishPostLoginFlow()
        await navigateAfterRoleChange(role, navigate, dispatch)
        return
      }

      try {
        const changeResult = await completeRoleChange(role, userData, dispatch)
        if (changeResult.status === 'otp_required') {
          clearTempUserRole()
          flushSync(() => {
            setShowRoleSelection(true)
          })
          return
        }
        finishPostLoginFlow()
        await navigateAfterRoleChange(changeResult.role, navigate, dispatch)
      } catch (error) {
        const errorMessage =
          typeof error === 'string' ? error : 'Failed to change role. Please try again.'
        setRoleError(errorMessage)
      }
      return
    }

    flushSync(() => {
      setShowRoleSelection(true)
    })
  }

  const onSubmit = async (data: LoginFormData) => {
    setRoleError(null)
    dispatch(clearError())
    clearTempUserRole()
    flushSync(() => {
      setIsPostLoginFlow(true)
    })

    try {
      const result = await dispatch(
        loginUser({
          email: data.email.trim().toLowerCase(),
          password: data.password,
        })
      ).unwrap()

      await handleLoginSuccess(result)
    } catch (error) {
      setIsPostLoginFlow(false)
      const errorMessage =
        typeof error === 'string' ? error : 'Login failed. Please try again.'

      if (errorMessage.includes('role') || errorMessage.includes('Role')) {
        setRoleError(errorMessage)
        return
      }

      // Angular errorParse → notify(error, 0) / notifyError — login-only Validation popup
      const popup = resolveLoginErrorPopup(errorMessage)
      showLoginValidationMessage(popup.message, popup.title)
      dispatch(clearError())
    }
  }

  return (
    <>
      {showRoleSelection && (
        <RoleSelectionDialog
          isOpen={showRoleSelection}
          fromLogin
          onClose={() => {
            setShowRoleSelection(false)
          }}
          onRoleChangeSuccess={handleRoleChangeSuccess}
        />
      )}

      <div className="min-h-screen bg-gray-100">
        <SandboxBanner />

        <div
          className={`flex items-center justify-center px-3 py-4 pb-24 sm:p-4 sm:pb-24 ${
            isSandboxEnvironment()
              ? 'min-h-[calc(100vh-40px)]'
              : 'min-h-screen'
          }`}
        >
          <div className="relative w-full max-w-6xl overflow-hidden rounded-lg bg-white shadow-xl">
            {showLoginSpinner && (
              <IosLoadingOverlay
                label="Signing in…"
                data-testid="login-signing-in-overlay"
              />
            )}
            <div className="flex flex-col md:min-h-[560px] md:flex-row lg:min-h-[650px]">
              <div className="flex flex-col items-center justify-center bg-gray-50 px-6 py-8 sm:px-10 sm:py-10 md:flex-1 md:p-12">
                <div className="text-center">
                  <img
                    src={loginLogoSrc}
                    alt={loginCopy.logo.title}
                    className="mx-auto h-16 w-auto sm:h-24 md:mb-4 md:h-32 lg:h-40"
                    onError={() => {
                      if (loginLogoSrc !== loginCopy.defaultLogoSrc) {
                        setLoginLogoSrc(loginCopy.defaultLogoSrc)
                      }
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-col justify-center px-5 py-8 sm:px-8 sm:py-10 md:flex-1 md:p-10 lg:p-12">
                {roleError && (
                  <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                    <div>
                      <p className="font-medium text-red-800">{loginCopy.accessDenied}</p>
                      <p className="text-sm text-red-600">{roleError}</p>
                    </div>
                  </div>
                )}

                <div className="mb-6 sm:mb-8">
                  <h1 className="text-2xl font-bold text-teal-dark sm:text-3xl">
                    {loginCopy.form.welcomeBack}
                  </h1>
                  <p className="mt-2 text-sm text-gray-500">
                    {loginCopy.form.welcomeSubtitle}
                  </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6">
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      {loginCopy.form.emailLabel}
                    </label>
                    <input
                      id="email"
                      type="email"
                      {...register('email')}
                      disabled={showLoginSpinner}
                      className={cn(
                        'w-full rounded-md border px-4 py-3 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500',
                        errors.email ? 'border-red-500' : 'border-gray-300',
                        showLoginSpinner && 'cursor-not-allowed opacity-50'
                      )}
                      placeholder={loginCopy.form.emailPlaceholder}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      {loginCopy.form.passwordLabel}
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        {...register('password')}
                        disabled={showLoginSpinner}
                        className={cn(
                          'w-full rounded-md border px-4 py-3 pr-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500',
                          errors.password ? 'border-red-500' : 'border-gray-300',
                          showLoginSpinner && 'cursor-not-allowed opacity-50'
                        )}
                        placeholder={loginCopy.form.passwordPlaceholder}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Tooltip
                          content={showPassword ? 'Hide password' : 'Show password'}
                          side="top"
                        >
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="text-gray-500 hover:text-gray-700"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <Eye className="h-5 w-5" />
                            ) : (
                              <EyeOff className="h-5 w-5" />
                            )}
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                    )}
                    <div className="mt-2 flex justify-end">
                      <Link
                        to={
                          typedEmail?.trim()
                            ? `/forgot-password?email=${encodeURIComponent(typedEmail.trim())}`
                            : '/forgot-password'
                        }
                        className="text-sm font-medium text-teal-dark hover:text-teal-darker"
                      >
                        {loginCopy.form.forgotPassword}
                      </Link>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={showLoginSpinner}
                    className={cn(
                      'relative flex w-full items-center justify-center gap-2 rounded-xl bg-teal-dark px-6 py-3 text-sm font-semibold tracking-wide text-white transition-all',
                      'hover:bg-teal-darker active:scale-[0.99]',
                      'disabled:cursor-not-allowed disabled:opacity-70',
                    )}
                  >
                    <span className="text-base font-bold uppercase tracking-wide">
                      {showLoginSpinner
                        ? loginCopy.form.signingInButton
                        : loginCopy.form.signInButton}
                    </span>
                  </button>

                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white px-3 text-center text-gray-500">
                        {loginCopy.form.newToLibertyRent}
                      </span>
                    </div>
                  </div>

                  <Link
                    to="/apply-now"
                    className="flex w-full items-center justify-center rounded-md border-2 border-teal-dark bg-white px-6 py-3 text-base font-bold uppercase tracking-wide text-teal-dark transition-colors hover:bg-teal-50"
                  >
                    {loginCopy.form.applyNow}
                  </Link>
                </form>
              </div>
            </div>

            <div className="border-t border-gray-200 px-4 py-4 text-center sm:px-6 sm:py-5">
              <p className="text-xs text-gray-400 sm:text-sm">{getAuthCopyright()}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
